/**
 * WeightValidationDrawer — боковая панель для проверки весов целей.
 *
 * Показывает разбивку по сотрудникам: сумма весов, статус (✅/❌),
 * кол-во целей, список проблем.
 */
import { useState, useCallback, useEffect } from 'react';
import {
  Drawer,
  Table,
  Tag,
  Statistic,
  Alert,
  Button,
  Space,
  Typography,
  Spin,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { WeightValidationEntry, WeightValidationResponse } from '@lens/shared';
import { validateWeights } from './api.ts';

const { Text } = Typography;

interface WeightValidationDrawerProps {
  open: boolean;
  onClose: () => void;
  spaceId: string;
  itemTypeId: string;
  quarter: string;
}

const ISSUE_LABELS: Record<string, string> = {
  sum_not_100: 'Сумма ≠ 100%',
  weight_not_multiple_5: 'Вес не кратен 5',
  no_goals: 'Нет целей',
};

export function WeightValidationDrawer({
  open,
  onClose,
  spaceId,
  itemTypeId,
  quarter,
}: WeightValidationDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WeightValidationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchValidation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await validateWeights(spaceId, { itemTypeId, quarter });
      setData(res);
    } catch (err: any) {
      setError(err.message ?? 'Ошибка валидации');
    } finally {
      setLoading(false);
    }
  }, [spaceId, itemTypeId, quarter]);

  useEffect(() => {
    if (open) {
      void fetchValidation();
    }
  }, [open, fetchValidation]);

  const columns = [
    {
      title: 'Сотрудник',
      key: 'person',
      render: (_: unknown, record: WeightValidationEntry) => (
        <Text strong>{record.personName}</Text>
      ),
    },
    {
      title: 'Целей',
      dataIndex: 'goalCount',
      key: 'goalCount',
      width: 70,
      align: 'center' as const,
    },
    {
      title: 'Σ вес',
      key: 'totalWeight',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: WeightValidationEntry) => (
        <Text
          strong
          style={{
            color: record.totalWeight === 100 ? 'var(--color-success)' : 'var(--color-error)',
          }}
        >
          {record.totalWeight}%
        </Text>
      ),
    },
    {
      title: 'Статус',
      key: 'status',
      width: 100,
      render: (_: unknown, record: WeightValidationEntry) =>
        record.isValid ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>OK</Tag>
        ) : (
          <Tag color="error" icon={<CloseCircleOutlined />}>Ошибка</Tag>
        ),
    },
    {
      title: 'Проблемы',
      key: 'issues',
      width: 180,
      render: (_: unknown, record: WeightValidationEntry) => (
        <Space size={4} wrap>
          {record.issues.map((issue) => (
            <Tag key={issue} color="warning" icon={<WarningOutlined />}>
              {ISSUE_LABELS[issue] ?? issue}
            </Tag>
          ))}
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>⚖ Проверка весов — {quarter}</span>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => void fetchValidation()}
            loading={loading}
          >
            Обновить
          </Button>
        </div>
      }
      open={open}
      onClose={onClose}
      width={640}
      data-testid="weight-validation-drawer"
    >
      {loading && !data && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {data && (
        <>
          <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
            <Statistic
              title="Всего"
              value={data.stats.totalPeople}
              valueStyle={{ fontSize: 20 }}
            />
            <Statistic
              title="Корректно"
              value={data.stats.validPeople}
              valueStyle={{ fontSize: 20, color: 'var(--color-success)' }}
              prefix={<CheckCircleOutlined />}
            />
            <Statistic
              title="С ошибками"
              value={data.stats.invalidPeople}
              valueStyle={{
                fontSize: 20,
                color: data.stats.invalidPeople > 0 ? 'var(--color-error)' : 'var(--color-success)',
              }}
              prefix={data.stats.invalidPeople > 0 ? <CloseCircleOutlined /> : undefined}
            />
          </div>

          {data.allValid && (
            <Alert
              type="success"
              message="Все веса корректны"
              description="Сумма весов каждого сотрудника = 100%, все веса кратны 5."
              style={{ marginBottom: 16 }}
            />
          )}

          <Table
            dataSource={data.entries}
            columns={columns}
            rowKey="personEntryId"
            size="small"
            pagination={data.entries.length > 30 ? { pageSize: 30 } : false}
            rowClassName={(record: WeightValidationEntry) =>
              record.isValid ? '' : 'ant-table-row-error'
            }
          />
        </>
      )}
    </Drawer>
  );
}
